import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { monitorAPI, formsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import DataGrid from '../components/Common/DataGrid';

// Local summary cards component
const SummaryCards: React.FC = () => {
  const { data, isLoading } = useQuery('usage-summary-cards', monitorAPI.getSummaryCards, { refetchOnWindowFocus: false });
  const { data: forms } = useQuery('forms-for-usage-card', () => formsAPI.getAllForms(), { refetchOnWindowFocus: false });
  const totalUsage = Number(data?.totalUsageGB ?? 0);
  const totalCustomers = data?.totalCustomers ?? 0;
  // Handle paginated response structure: { data: ServiceForm[], pagination: {...} }
  const formsArray = Array.isArray(forms) ? forms : (forms as any)?.data;
  const currentMonthUsageValue = Array.isArray(formsArray) ? formsArray.reduce((acc: number, f: any) => acc + (Number(f.currentUsage) || 0), 0) : 0;
  
  // Calculate grand total by adding current month's usage to total usage
  const grandTotal = totalUsage + currentMonthUsageValue;
  
  const currentMonthUsageFormatted = currentMonthUsageValue.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const grandTotalFormatted = grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="p-6 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="text-sm text-gray-600">Total Data Usage (GB)</div>
        <div className="text-3xl font-extrabold text-gray-900 mt-1">{isLoading ? '—' : grandTotalFormatted}</div>
      </div>
      <div className="p-6 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="text-sm text-gray-600">Total Customers</div>
        <div className="text-3xl font-extrabold text-gray-900 mt-1">{isLoading ? '—' : totalCustomers}</div>
      </div>
      <div className="p-6 rounded-2xl border border-gray-100 relative bg-white shadow-sm">
        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg absolute top-4 right-4">
          <i className="fas fa-chart-pie text-xl"></i>
        </div>
        <div className="pr-16">
          <div className="flex items-baseline space-x-2">
            <p className="text-4xl font-bold text-gray-900 whitespace-nowrap">{isLoading ? '—' : currentMonthUsageFormatted}</p>
            <span className="text-xl font-bold text-gray-900">GB</span>
          </div>
          <p className="text-gray-600 font-medium mt-2">Current Month's data usage</p>
        </div>
      </div>
    </div>
  );
};

const DataUsage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'insights' | 'history'>('insights');

  const { refetch, isFetching } = useQuery(
    'current-month-usage',
    () => monitorAPI.getCurrentMonthTotal(),
    { refetchOnWindowFocus: false }
  );

  const { data: history, isLoading: historyLoading } = useQuery(
    'usage-history',
    () => monitorAPI.getUsageHistory(12),
    { refetchOnWindowFocus: false }
  );

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // YYYY-MM
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ status: string; currentIndex: number; totalMonths: number; completedBatches: number; totalBatches: number; phase1: number; phase2: number; message?: string } | null>(null);

  useEffect(() => {
    if (!modalOpen) return;
    monitorAPI.getAccountId().then(setAccountId).catch(() => setAccountId(null));
  }, [modalOpen]);

  useEffect(() => {
    if (!jobId) return;
    let consecutiveNetworkErrors = 0;
    const id = setInterval(async () => {
      try {
        const p = await monitorAPI.getRangeAggregationProgress(jobId);
        consecutiveNetworkErrors = 0;
        setProgress(p as any);
        if (p.status === 'done' || p.status === 'error') {
          clearInterval(id);
          queryClient.invalidateQueries('usage-summary-cards');
          queryClient.invalidateQueries('usage-history');
          queryClient.invalidateQueries('current-month-usage');
        }
      } catch (err: any) {
        const isNetwork = err?.message?.includes('Network') || err?.code === 'ERR_NETWORK';
        const status = err?.response?.status;
        const is404 = status === 404; // job map lost after backend restart
        const is401 = status === 401; // token expired; allow refresh grace
        if (isNetwork || is404 || is401) {
          consecutiveNetworkErrors += 1;
          // If we have a few consecutive failures, assume job finished and refresh
          if (consecutiveNetworkErrors >= 3) {
            clearInterval(id);
            queryClient.invalidateQueries('usage-summary-cards');
            queryClient.invalidateQueries('usage-history');
            queryClient.invalidateQueries('current-month-usage');
            setProgress({ status: 'done', currentIndex: 0, totalMonths: 0, completedBatches: 0, totalBatches: 0, phase1: 100, phase2: 100, message: 'Completed (backend restarted)' } as any);
            return;
          }
          return; // transient network issue—try again on next tick
        }
        // Other errors: stop and surface to user
        clearInterval(id);
        setProgress({ status: 'error', currentIndex: 0, totalMonths: 0, completedBatches: 0, totalBatches: 0, phase1: 0, phase2: 0, message: err?.message || 'Progress fetch failed' });
        setJobId(null);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [jobId, queryClient, refetch]);

  const historyRows = Array.isArray(history) ? history : [];
  const chronologicalHistory = [...historyRows].sort((left, right) => {
    const leftValue = new Date(Date.UTC(left.year, left.month - 1, 1)).getTime();
    const rightValue = new Date(Date.UTC(right.year, right.month - 1, 1)).getTime();
    return leftValue - rightValue;
  });

  const maxUsageValue = chronologicalHistory.reduce((max, row) => Math.max(max, Number(row.totalPriorityDataGB) || 0), 0);
  const latestHistory = chronologicalHistory[chronologicalHistory.length - 1];
  const previousHistory = chronologicalHistory[chronologicalHistory.length - 2];
  const averageUsage = chronologicalHistory.length > 0
    ? chronologicalHistory.reduce((sum, row) => sum + (Number(row.totalPriorityDataGB) || 0), 0) / chronologicalHistory.length
    : 0;
  const peakUsageRow = chronologicalHistory.reduce((peak: any, row: any) => {
    if (!peak || Number(row.totalPriorityDataGB) > Number(peak.totalPriorityDataGB)) {
      return row;
    }
    return peak;
  }, null);
  const latestUsageValue = Number(latestHistory?.totalPriorityDataGB || 0);
  const previousUsageValue = Number(previousHistory?.totalPriorityDataGB || 0);
  const monthOverMonthChange = previousUsageValue > 0
    ? ((latestUsageValue - previousUsageValue) / previousUsageValue) * 100
    : 0;
  const sentEmailCount = chronologicalHistory.filter((row: any) => row.emailStatus === 'sent').length;
  const pendingEmailCount = chronologicalHistory.filter((row: any) => row.emailStatus !== 'sent').length;

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setJobId(null);
    setProgress(null);
  };

  const startProcessing = async () => {
    if (!selectedMonth) return;
    const [y, m] = selectedMonth.split('-').map((v) => Number(v));
    const payload = { accountId: accountId || undefined, startYear: y, startMonth: m };
    const { jobId } = await monitorAPI.startRangeAggregation(payload);
    setJobId(jobId);
  };

  return (
    <>
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(245,158,11,0.26),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(56,189,248,0.18),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.14)]" />
              Usage analytics
          </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Data Usage</h1>
            <p className="max-w-2xl text-white/70">Overview of current and historical aggregated usage.</p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/usage/exports')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-file-export"></i>
              Exports
            </button>
            {user?.hasAdminAccess && (
              <button
                onClick={openModal}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
              >
                <i className="fas fa-sync"></i>
                Get Data Usage
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-black text-gray-900">Overview</h2>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-800 hover:bg-gray-200 transition-all"
            disabled={isFetching}
          >
            <i className={`fas fa-rotate-right ${isFetching ? 'animate-spin' : ''}`}></i>
            Refresh
            </button>
        </div>
        <SummaryCards />
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {[
            { id: 'insights', label: 'Insights', hint: 'Charts, trends, highlights' },
            { id: 'history', label: 'Monthly history', hint: 'Table and computed records' },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'insights' | 'history')}
                className={`min-w-[12rem] flex-1 rounded-2xl border px-4 py-3 text-left transition-all ${
                  isActive
                    ? 'border-slate-900 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white shadow-lg'
                    : 'border-gray-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-bold">{tab.label}</div>
                <p className={`mt-1 text-xs ${isActive ? 'text-white/75' : 'text-slate-500'}`}>{tab.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'insights' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-black text-gray-900">Trend highlights</h2>
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Latest snapshot</p>
                  <p className="mt-2 text-2xl font-black text-slate-900">
                    {latestUsageValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB
                  </p>
                  <p className="text-sm text-slate-600">
                    {latestHistory
                      ? new Date(Date.UTC(latestHistory.year, latestHistory.month - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
                      : 'No snapshot yet'}
                  </p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Month-on-month</p>
                  <p className={`mt-2 text-2xl font-black ${monthOverMonthChange >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {previousHistory ? `${monthOverMonthChange >= 0 ? '+' : ''}${monthOverMonthChange.toFixed(1)}%` : '—'}
                  </p>
                  <p className="text-sm text-slate-600">Compared with the previous computed month.</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Average monthly usage</p>
                  <p className="mt-2 text-2xl font-black text-slate-900">
                    {averageUsage.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB
                  </p>
                  <p className="text-sm text-slate-600">Across the current historical window.</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-black text-gray-900">Operational pulse</h2>
              <div className="mt-5 space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-700">Sent summary emails</span>
                    <span className="text-slate-500">{sentEmailCount}</span>
                  </div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400"
                      style={{ width: `${chronologicalHistory.length > 0 ? (sentEmailCount / chronologicalHistory.length) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-700">Pending summary emails</span>
                    <span className="text-slate-500">{pendingEmailCount}</span>
                  </div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-400"
                      style={{ width: `${chronologicalHistory.length > 0 ? (pendingEmailCount / chronologicalHistory.length) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-100 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Peak month</p>
                  <p className="mt-2 text-lg font-black text-slate-900">
                    {peakUsageRow
                      ? new Date(Date.UTC(peakUsageRow.year, peakUsageRow.month - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
                      : 'No data'}
                  </p>
                  <p className="text-sm text-slate-700">
                    {peakUsageRow
                      ? `${Number(peakUsageRow.totalPriorityDataGB).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB consumed`
                      : 'Run aggregation to start building usage intelligence.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-black text-gray-900">Usage trend</h2>
                <p className="mt-1 text-sm text-gray-500">Monthly aggregated usage over the last 12 snapshots.</p>
              </div>
              <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                {chronologicalHistory.length} months
              </div>
            </div>

            {historyLoading ? (
              <div className="flex h-64 items-center justify-center text-gray-500">Loading chart…</div>
            ) : chronologicalHistory.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-gray-500">No usage history yet.</div>
            ) : (
              <div className="mt-6">
                <div className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <span>Full timeline</span>
                  <span>Scroll horizontally to see more months</span>
                </div>
                <div className="overflow-x-auto pb-2">
                  <div className="min-w-[980px] rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-6">
                    <div className="flex h-80 items-end gap-4">
                      {chronologicalHistory.map((row: any) => {
                        const usageValue = Number(row.totalPriorityDataGB) || 0;
                        const heightPercent = maxUsageValue > 0 ? Math.max((usageValue / maxUsageValue) * 100, 6) : 6;
                        const dateLabel = new Date(Date.UTC(row.year, row.month - 1, 1)).toLocaleDateString(undefined, {
                          month: 'short',
                          year: 'numeric',
                        });
                        const isLatest = latestHistory && row.year === latestHistory.year && row.month === latestHistory.month;

                        return (
                          <div key={`${row.year}-${row.month}`} className="flex min-w-[88px] flex-col items-center justify-end gap-3">
                            <div className="text-center text-[11px] font-semibold leading-4 text-white/80">
                              {usageValue.toLocaleString('en-US', { maximumFractionDigits: 0 })} GB
                            </div>
                            <div className="flex h-52 w-full items-end justify-center rounded-2xl bg-white/5 px-2 py-2">
                              <div
                                className={`w-full rounded-2xl ${isLatest ? 'bg-gradient-to-t from-amber-400 via-orange-400 to-yellow-300' : 'bg-gradient-to-t from-cyan-500 via-blue-400 to-sky-300'} shadow-[0_12px_28px_rgba(15,23,42,0.28)]`}
                                style={{ height: `${heightPercent}%` }}
                              />
                            </div>
                            <div className="text-center text-xs font-semibold leading-4 text-white/70">{dateLabel}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-black text-gray-900">Month-by-month snapshots</h2>
                <p className="mt-1 text-sm text-gray-500">Quick scan of customer volume, usage totals, and notification state.</p>
              </div>
            </div>
            {historyLoading ? (
              <div className="flex h-24 items-center text-gray-500">Loading snapshot cards…</div>
            ) : chronologicalHistory.length === 0 ? (
              <div className="flex h-24 items-center text-gray-500">No historical records yet.</div>
            ) : (
              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {chronologicalHistory.slice().reverse().map((row: any) => (
                  <div key={`${row.year}-${row.month}`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-black text-slate-900">
                        {new Date(Date.UTC(row.year, row.month - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                      </h3>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${row.emailStatus === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {row.emailStatus === 'sent' ? 'Sent' : 'Pending'}
                      </span>
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-slate-600">
                      <div className="flex items-center justify-between">
                        <span>Customers</span>
                        <span className="font-semibold text-slate-900">{row.totalServiceLines ?? 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Usage</span>
                        <span className="font-semibold text-slate-900">
                          {Number(row.totalPriorityDataGB).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Computed</span>
                        <span className="font-semibold text-slate-900">{new Date(row.computedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <DataGrid
          title="Monthly history"
          description={`${historyRows.length} computed records`}
          rows={historyRows}
          emptyText="No history yet"
          minWidthClassName="w-full table-fixed"
          columns={[
            {
              key: 'month',
              header: 'Month',
              className: 'w-[22%]',
              render: (row: any) => (
                <span className="font-semibold text-gray-900">
                  {new Date(Date.UTC(row.year, row.month - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </span>
              ),
            },
            {
              key: 'customers',
              header: 'Total Customers',
              className: 'w-[14%] text-right',
              render: (row: any) => <span className="block text-right font-semibold text-gray-900">{row.totalServiceLines ?? 0}</span>,
            },
            {
              key: 'usage',
              header: 'Total Data Usage (GB)',
              className: 'w-[20%] text-right',
              render: (row: any) => (
                <span className="block text-right font-semibold text-gray-900">
                  {Number(row.totalPriorityDataGB).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              ),
            },
            {
              key: 'computedAt',
              header: 'Computed At',
              className: 'w-[26%]',
              render: (row: any) => new Date(row.computedAt).toLocaleString(),
            },
            {
              key: 'emailStatus',
              header: 'Email Status',
              className: 'w-[18%]',
              render: (row: any) => (
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${row.emailStatus === 'sent' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {row.emailStatus === 'sent' ? 'Sent' : 'Pending'}
                </span>
              ),
            },
          ]}
        />
      )}

    </div>
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Get Data Usage</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeModal}><i className="fas fa-times"></i></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                <input type="text" readOnly value={accountId ?? ''} className="w-full border rounded-lg px-3 py-2 bg-gray-50" />
                {!accountId && <div className="text-xs text-gray-500 mt-1">Define STARLINK_ACCOUNT_ID in backend env to auto-fill.</div>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Start Month</label>
                <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="border rounded-lg px-3 py-2" />
                <div className="text-xs text-gray-500 mt-1">Will compute for each month from the selected month up to current month.</div>
              </div>
              <div className="space-x-3">
                <button onClick={startProcessing} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50" disabled={!selectedMonth || !!jobId}>
                  Start
                </button>
              </div>
              {jobId && (
                <div className="space-y-4 mt-4">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Fetching batches ({progress?.completedBatches ?? 0}/{progress?.totalBatches ?? 0})</span>
                      <span>{progress?.phase1 ?? 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-blue-600 h-3 rounded-full" style={{ width: `${progress?.phase1 ?? 0}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Processing JSON artifact</span>
                      <span>{progress?.phase2 ?? 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-amber-600 h-3 rounded-full" style={{ width: `${progress?.phase2 ?? 0}%` }} />
                    </div>
                  </div>
                  {progress?.status === 'done' && (
                    <div className="flex justify-end">
                      <button onClick={closeModal} className="px-4 py-2 bg-green-600 text-white rounded-lg">Done</button>
                    </div>
                  )}
                  {progress?.status === 'error' && (
                    <div className="text-red-600 text-sm">Failed: {progress?.message}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
  </>
  );
};

export default DataUsage;
